import React, { useEffect, useState } from 'react';
import AdminDashboard from 'layouts/AdminDashboard';
import { Box, Breadcrumbs } from '@mui/material';
import {
  getInvitation,
  saveInvitation,
  updateInvitation,
} from './InvitationService';
import { useNavigate, useParams } from 'react-router-dom';
import Container from 'components/Container';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

import { useLocation } from 'react-router-dom';
import { notify } from 'utils/notify';
import { extractErrorMessage } from 'utils/extractErrorMessage';

const InvitationDetail = (): JSX.Element => {
  // Extract membershipid, subscriptionid, and useLocation for query params
  const { membershipid, subscriptionid } = useParams();
  const location = useLocation();

  // Extract groupName from the query params
  const queryParams = new URLSearchParams(location.search);
  const groupName = queryParams.get('groupName'); // Get 'groupName' from query string

  // State and handlers for the form
  const [sender, setSender] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [status, setStatus] = useState('');

  const navigate = useNavigate();
  const { invitationid } = useParams();

  // Function to clear the invitation form
  function clearInvitationDetailsForm() {
    setSender('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setStatus('');
  }

  // Navigate back to the previous page
  const cancelSaveOrUpdateInvitation = () => {
    clearInvitationDetailsForm();
    navigate(
      `/admin/dashboard/membership/${membershipid}/subscription/${subscriptionid}`,
    );
  };

  // Handle the save or update action
  function saveOrUpdateInvitation(e) {
    e.preventDefault();

    const invitationData = { sender, email, firstName, lastName, status };

    // Call the saveInvitation function with groupName
    if (invitationid) {
      updateInvitation(invitationid, invitationData)
        .then(() => {
          navigate(
            `/admin/dashboard/membership/${membershipid}/subscription/${subscriptionid}`,
          );
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    } else {
      saveInvitation(invitationData, groupName)
        .then(() => {
          navigate('/admin/dashboard/invitation');
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  }

  // Fetch existing invitation details if invitationid exists
  useEffect(() => {
    if (invitationid) {
      getInvitation(membershipid, subscriptionid, invitationid)
        .then((response) => {
          setSender(response.data.sender);
          setEmail(response.data.email);
          setFirstName(response.data.firstName);
          setLastName(response.data.lastName);
          setStatus(response.data.status);
        })
        .catch((error) => {
          console.error(error);
          notify(extractErrorMessage(error));
        });
    }
  }, [invitationid, membershipid, subscriptionid]);

  return (
    <Container>
      <AdminDashboard>
        <Box marginTop={2} marginBottom={4}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/">
              System Content
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href={'/system/dashboard/accounts'}
            >
              Invitations
            </Link>
            <Typography color="text.primary">Invitation Detail</Typography>
          </Breadcrumbs>
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginBottom={4}
        >
          <Typography variant="h4">Invitation Detail</Typography>
        </Box>

        {/* Display the groupName at the top of the form */}
        {groupName && (
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Group: {groupName}
          </Typography>
        )}

        <Box>
          <form>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Sender
                </Typography>
                <TextField
                  variant="outlined"
                  name={'sender'}
                  fullWidth
                  value={sender}
                  onChange={(event) => setSender(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Email
                </Typography>
                <TextField
                  variant="outlined"
                  name={'email'}
                  fullWidth
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  First Name
                </Typography>
                <TextField
                  variant="outlined"
                  name={'firstName'}
                  fullWidth
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Last Name
                </Typography>
                <TextField
                  variant="outlined"
                  name={'lastName'}
                  fullWidth
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant={'subtitle2'}
                  sx={{ marginBottom: 2 }}
                  fontWeight={700}
                >
                  Status
                </Typography>
                <TextField
                  variant="outlined"
                  name={'status'}
                  fullWidth
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                />
              </Grid>
              <Grid item container xs={12}>
                <Box
                  display="flex"
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretched', sm: 'center' }}
                  justifyContent={'space-between'}
                  width={1}
                  marginBottom={4}
                >
                  <Button onClick={cancelSaveOrUpdateInvitation}>Cancel</Button>
                  <Button onClick={saveOrUpdateInvitation}>Send Invite</Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </AdminDashboard>
    </Container>
  );
};

export default InvitationDetail;
